import { Button, ButtonProps } from '@/swell-ui/Button'
import { CircularProgress } from '@/swell-ui/CircularProgress'
import styled from 'styled-components'
import { ConnectWalletButton } from '@/components/ConnectWalletButton'
import { SwitchChainButton } from '@/components/SwitchChainButton'
import { useWeb3Button } from '@/hooks/useWeb3Button'
import {
  NucleusApproveAssetForDeposit,
  NucleusApproveVaultTokenForAtomicQueue,
  NucleusCancelWithdraw,
  NucleusDeposit,
  NucleusRequestWithdraw,
} from '@/state/nucleusVault/hooks'
import {
  PreparedNucleusApproveAssetForDeposit,
  PreparedNucleusApproveVaultTokenForAtomicQueue,
  PreparedNucleusCancelWithdraw,
  PreparedNucleusDeposit,
  PreparedNucleusRequestWithdraw,
} from './nucleusCalls'
import { INucleusActiveWithdrawal } from './nucleusHooks'

const StyledButtonThin = styled(Button)`
  width: 100%;
  height: unset;
  /* Body/medium bold */
  font-family: Inter;
  font-size: 16px;
  font-style: normal;
  font-weight: 600;
  line-height: 160%; /* 25.6px */
  padding: 8px 16px;
  height: 42px;

  &:disabled {
    opacity: 0.2;
  }
`

const StyledConnectButton = styled(ConnectWalletButton)`
  width: 100%;
`
const StyledSwitchChainButton = styled(SwitchChainButton)`
  width: 100%;
`

export function NucleusButton(props: ButtonProps) {
  return (
    <StyledButtonThin
      variant="primary"
      onClick={props.onClick}
      disabled={props.disabled}
    >
      {props.children}
    </StyledButtonThin>
  )
}

export function NucleusConnectButton(props: ButtonProps) {
  return <StyledConnectButton {...props} variant="primary" />
}
export function NucleusSwitchChainButton() {
  return <StyledSwitchChainButton variant="primary" />
}
const ButtonCircularProgress = styled(CircularProgress)`
  &.MuiCircularProgress-colorPrimary {
    color: currentColor;
  }
`
const ButtonInner = styled.span`
  position: relative;
  > div {
    position: absolute;
    top: -7px;
    left: -32px;
  }
`
export function NucleusApproveAssetForDepositButton({
  approveAssetForDeposit,
  prepared,
  preventInteraction,
}: {
  approveAssetForDeposit: NucleusApproveAssetForDeposit
  prepared: PreparedNucleusApproveAssetForDeposit
  preventInteraction?: boolean
}) {
  const btn = useWeb3Button(
    approveAssetForDeposit,
    approveAssetForDeposit.call,
    prepared.args,
    {
      idleLabel: 'Approve',
      pendingLabel: (
        <ButtonInner>
          <div>
            <ButtonCircularProgress size={24} />
          </div>
          <span>Confirming...</span>
        </ButtonInner>
      ),
      fulfilledLabel: 'Approve',
    }
  )

  return (
    <NucleusButton
      disabled={preventInteraction || btn.disabled}
      onClick={btn.onClick}
    >
      {btn.label}
    </NucleusButton>
  )
}
export function NucleusApproveVaultTokenForAtomicQueueButton({
  approveVaultTokenForAtomicQueue,
  prepared,
  preventInteraction,
}: {
  approveVaultTokenForAtomicQueue: NucleusApproveVaultTokenForAtomicQueue
  prepared: PreparedNucleusApproveVaultTokenForAtomicQueue
  preventInteraction: boolean
}) {
  const btn = useWeb3Button(
    approveVaultTokenForAtomicQueue,
    approveVaultTokenForAtomicQueue.call,
    prepared.args,
    {
      idleLabel: 'Approve',
      pendingLabel: (
        <ButtonInner>
          <div>
            <ButtonCircularProgress size={24} />
          </div>
          <span>Confirming...</span>
        </ButtonInner>
      ),
      fulfilledLabel: 'Approve',
    }
  )
  return (
    <NucleusButton
      disabled={preventInteraction || btn.disabled}
      onClick={btn.onClick}
    >
      {btn.label}
    </NucleusButton>
  )
}

export function NucleusDepositButton({
  deposit,
  prepared,
  preventInteraction,
}: {
  deposit: NucleusDeposit
  prepared: PreparedNucleusDeposit
  preventInteraction: boolean
}) {
  const btn = useWeb3Button(deposit, deposit.call, prepared.args, {
    idleLabel: 'Deposit',
    pendingLabel: (
      <ButtonInner>
        <div>
          <ButtonCircularProgress size={24} />
        </div>
        <span>Confirming...</span>
      </ButtonInner>
    ),
    fulfilledLabel: 'Deposit',
  })

  return (
    <NucleusButton
      disabled={preventInteraction || btn.disabled}
      onClick={btn.onClick}
    >
      {btn.label}
    </NucleusButton>
  )
}
export function NucleusRequestWithdrawButton({
  requestWithdraw,
  prepared,
  preventInteraction,
}: {
  requestWithdraw: NucleusRequestWithdraw
  prepared: PreparedNucleusRequestWithdraw
  preventInteraction: boolean
}) {
  const btn = useWeb3Button(
    requestWithdraw,
    requestWithdraw.call,
    prepared.args,
    {
      idleLabel: 'Request withdrawal',
      pendingLabel: (
        <ButtonInner>
          <div>
            <ButtonCircularProgress size={24} />
          </div>
          <span>Confirming...</span>
        </ButtonInner>
      ),
      fulfilledLabel: 'Requested withdrawal',
    }
  )

  return (
    <NucleusButton
      disabled={preventInteraction || btn.disabled}
      onClick={btn.onClick}
    >
      {btn.label}
    </NucleusButton>
  )
}

export function NucleusCancelWithdrawButton({
  cancelWithdraw,
  prepared,
  preventInteraction,
}: {
  cancelWithdraw: NucleusCancelWithdraw
  prepared: PreparedNucleusCancelWithdraw
  preventInteraction: boolean
}) {
  const btn = useWeb3Button(
    cancelWithdraw,
    cancelWithdraw.call,
    prepared.args,
    {
      idleLabel: 'Cancel withdrawal',
      pendingLabel: (
        <ButtonInner>
          <div>
            <ButtonCircularProgress size={24} />
          </div>
          <span>Confirming...</span>
        </ButtonInner>
      ),
      fulfilledLabel: 'Canceled',
    }
  )

  return (
    <NucleusButton
      disabled={preventInteraction || btn.disabled}
      onClick={btn.onClick}
    >
      {btn.label}
    </NucleusButton>
  )
}

export function NucleusWithdrawFulfilledAckButton({
  activeWithdrawal,
}: {
  activeWithdrawal: INucleusActiveWithdrawal
}) {
  return (
    <NucleusButton onClick={activeWithdrawal.ackCompleted}>OK</NucleusButton>
  )
}

export function NucleusWithdrawFailedAckButton({
  activeWithdrawal,
}: {
  activeWithdrawal: INucleusActiveWithdrawal
}) {
  return <NucleusButton onClick={activeWithdrawal.ackFailed}>OK</NucleusButton>
}
